import React, { useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { LOPDAOTAOLIENTUC_TRIENKHAI_API, ACCOUNT_API, ACCOUNT_AUTHINFO_API } from '../../../../utils/apiUrl';
import { Get, Post, DeleteSingle } from '../../../../utils/httpProvider';
import { TutorDepartmentCell } from '../../daoTaoThucHanh/components/TutorTab';

const TutorLienTucTab = ({ lopId }) => {
    const queryClient = useQueryClient();

    // Modal state for manual Tutor creation
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTutor, setNewTutor] = useState({ fullName: '', identityCard: '' });
    const [isSaving, setIsSaving] = useState(false);

    // 1. Fetch danh sách giảng viên hiện có và map thông tin SSO (chuẩn theo KhoaDaoTaoDetail.js)
    const { data: tutorsData = [], isLoading } = useQuery(
        ['giangViensLienTuc', lopId],
        async () => {
            const res = await Get(`${LOPDAOTAOLIENTUC_TRIENKHAI_API.GET_GIANGVIENS}/${lopId}`);
            const accountIds = res?.$values || res || [];

            const detailTasks = accountIds.map(async (accountId) => {
                try {
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, accountId);
                    const profile = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;

                    const lastName = profile?.lastName || profile?.LastName || "";
                    const middleName = profile?.middleName || profile?.MiddleName || "";
                    const firstName = profile?.firstName || profile?.FirstName || "";
                    const fullName = [lastName, middleName, firstName]
                        .map(n => n?.trim())
                        .filter(Boolean)
                        .join(" ");

                    return {
                        id: accountId,
                        accountId: accountId,
                        fullName: fullName || "Chưa xác định",
                        phoneNumber: profile?.phoneNumber || profile?.PhoneNumber || "",
                        identityCard: profile?.identityCardNumber || profile?.IdentityCardNumber || profile?.identityCard || profile?.IdentityCard || "",
                        pseudonym: profile?.pseudonym || profile?.Pseudonym || "",
                        email: profile?.email || profile?.Email || ""
                    };
                } catch (err) {
                    return { id: accountId, accountId: accountId, fullName: "Lỗi tải thông tin" };
                }
            });

            return await Promise.all(detailTasks);
        },
        { 
            enabled: !!lopId,
            staleTime: 0,
            cacheTime: 0
        }
    );

    // 2. Tra cứu tài khoản SSO qua CCCD/username (giống chuẩn TutorTab)
    const checkAccountExistsOnSso = async (username) => {
        if (!username) return null;
        try {
            const res = await Get(`${ACCOUNT_API.GET_PAGING_ACCOUNT}?Keyword=${username}`);
            const items = res?.data?.$values || res?.data || [];
            const found = items.find(acc => {
                const accUsername = acc.username || acc.Username || acc.userName || acc.UserName;
                return accUsername && accUsername.toLowerCase() === username.toLowerCase();
            });
            return found ? (found.id || found.Id) : null;
        } catch (err) {
            return null;
        }
    };

    // 3. Xử lý thêm giảng viên thủ công
    const handleOpenAddTutor = () => {
        setNewTutor({ fullName: '', identityCard: '' });
        setShowAddModal(true);
    };

    const handleSaveTutor = async () => {
        if (!newTutor.fullName || !newTutor.identityCard) {
            toast.warning("Họ tên và Số CCCD là bắt buộc.");
            return;
        }
        setIsSaving(true);
        try {
            const searchKey = newTutor.identityCard.trim();
            const resolvedId = await checkAccountExistsOnSso(searchKey);
            if (!resolvedId) {
                toast.error("Không tìm thấy tài khoản Giảng viên này trên hệ thống SSO. Vui lòng kiểm tra lại số CCCD.");
                return;
            }

            await Post(`${LOPDAOTAOLIENTUC_TRIENKHAI_API.ADD_GIANGVIEN_MANUAL}/${lopId}`, null, resolvedId);
            toast.success("Thêm giảng viên thành công!");
            setShowAddModal(false);
            setNewTutor({ fullName: '', identityCard: '' });
            queryClient.refetchQueries(['giangViensLienTuc', lopId]);
            queryClient.refetchQueries(['lopDaoTaoLienTucDetail', lopId]);
        } catch (err) {
            toast.error(err?.message || "Không thể thêm giảng viên.");
        } finally {
            setIsSaving(false);
        }
    };



    // 5. Mutation: Xóa giảng viên khỏi lớp
    const removeMutation = useMutation(
        (gvId) => DeleteSingle(`${LOPDAOTAOLIENTUC_TRIENKHAI_API.REMOVE_GIANGVIEN}/${lopId}/${gvId}`),
        {
            onSuccess: () => {
                toast.success("Đã xóa giảng viên khỏi lớp");
                queryClient.refetchQueries(['giangViensLienTuc', lopId]);
                queryClient.refetchQueries(['lopDaoTaoLienTucDetail', lopId]);
            },
            onError: () => toast.error("Lỗi khi xóa giảng viên")
        }
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
                <div>
                    <h6 className="mb-0 text-primary font-weight-bold">
                        <i className="fas fa-chalkboard-teacher mr-2"></i>
                        Đội Ngũ Giảng Viên Của Lớp ({tutorsData.length})
                    </h6>
                    <div className="text-muted small">Danh sách các giảng viên/bác sĩ được phân công giảng dạy cho lớp (tự động cập nhật khi phân lịch)</div>
                </div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                    <Button
                        variant="primary"
                        size="sm"
                        style={{ borderRadius: '8px', padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={handleOpenAddTutor}
                        disabled={!lopId}
                    >
                        <i className="fas fa-plus mr-2"></i> Thêm giảng viên vào lớp
                    </Button>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                <div className="table-responsive">
                    <Table striped bordered hover className="mb-0 align-middle">
                        <thead className="thead-light text-nowrap">
                            <tr>
                                <th style={{ width: 55 }} className="text-center">#</th>
                                <th style={{ minWidth: 200 }}>Họ và tên</th>
                                <th style={{ minWidth: 180 }}>Khoa / Đơn vị</th>
                                <th style={{ minWidth: 120 }}>Số điện thoại</th>
                                <th style={{ minWidth: 180 }}>Email</th>
                                <th className="text-center" style={{ width: 90 }}>Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan={6} className="text-center py-4">Đang tải danh sách giảng viên...</td></tr>
                            ) : tutorsData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-5 text-muted">
                                        <i className="fas fa-user-md fa-3x mb-3 text-muted" style={{ opacity: 0.5 }}></i>
                                        <h6>Chưa có giảng viên nào được phân công</h6>
                                        <p className="small mb-0">Khi phân lịch học cho học viên, giảng viên được chọn từ toàn viện sẽ tự động hiển thị tại đây.</p>
                                    </td>
                                </tr>
                            ) : (
                                tutorsData.map((gv, idx) => (
                                    <tr key={gv.id || idx}>
                                        <td className="text-center">{idx + 1}</td>
                                        <td className="font-weight-bold text-dark">{gv.fullName || "—"}</td>
                                        <td>
                                            <TutorDepartmentCell accountId={gv.accountId} />
                                        </td>
                                        <td>{gv.phoneNumber || "—"}</td>
                                        <td>{gv.email || "—"}</td>
                                        <td className="text-center">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                style={{ borderRadius: '6px' }}
                                                onClick={() => {
                                                    if (window.confirm(`Bạn có chắc muốn xóa giảng viên "${gv.fullName}" khỏi lớp?`)) {
                                                        removeMutation.mutate(gv.id);
                                                    }
                                                }}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>

            {/* Modal Thêm giảng viên thủ công chuẩn TutorTab */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="font-weight-bold">Thêm giảng viên hướng dẫn mới</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">
                                Họ và tên giảng viên <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ví dụ: Bác sĩ Nguyễn Văn B..."
                                value={newTutor.fullName}
                                onChange={(e) => setNewTutor({ ...newTutor, fullName: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">
                                Số CCCD giảng viên <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Nhập số CCCD để khớp tài khoản trên SSO..."
                                value={newTutor.identityCard}
                                onChange={(e) => setNewTutor({ ...newTutor, identityCard: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="light" onClick={() => setShowAddModal(false)} style={{ borderRadius: '8px' }}>
                        Hủy bỏ
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveTutor}
                        disabled={isSaving}
                        style={{ borderRadius: '8px' }}
                    >
                        {isSaving ? "Đang lưu..." : "Lưu thông tin"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default TutorLienTucTab;
